import React, {
  FunctionComponent,
  ComponentPropsWithoutRef,
  useMemo,
  useState,
  Fragment,
} from 'react';
import { Tab, Tabs } from '@rbx/ui';
import useMarkdownTabsStyles from './MarkdownTabs.styles';
import MarkdownTabItem from './MarkdownTabItem';

export type TabsProps = ComponentPropsWithoutRef<'div'>;

const MarkdownTabs: FunctionComponent<React.PropsWithChildren<TabsProps>> = ({ children }) => {
  if (!Array.isArray(children)) {
    throw new Error('Unexpected type of children received for Tabs.');
  }

  if (children.length < 2) {
    throw new Error('Tabs components should have at least 2 chlidren.');
  }

  children.forEach((child) => {
    if (child.type.name !== MarkdownTabItem.displayName) {
      throw new Error(
        `Tabs components can only contain TabItems as children, not ${child.type.name}s`,
      );
    }
  });

  const [value, setValue] = useState<number>(children[0].props.value ?? 0);
  const {
    classes: { tabs },
  } = useMarkdownTabsStyles();

  const handleChange = (_event: React.ChangeEvent<object>, newValue: number) => {
    setValue(newValue);
  };

  const contents = useMemo(() => {
    const foundChildren = children.filter((child) => child.props.value === value);
    if (foundChildren.length > 0) {
      return foundChildren[0];
    }
    // If the value was not found, treat it as an array index
    return children[value];
  }, [children, value]);

  return (
    <Fragment>
      <Tabs className={tabs} value={value} onChange={handleChange}>
        {children.map((child, index) => {
          const tabValue = child.props.value ?? index;
          return (
            <Tab
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              label={child.props.label}
              value={tabValue}
            />
          );
        })}
      </Tabs>
      {contents}
    </Fragment>
  );
};

export default MarkdownTabs;
